import React, { memo } from "react";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { Link } from "@UI/Components/Link/Link";
import { FaceViewfinderIcon } from "@UI/Components/Icons/FaceViewfinderIcon";
import { IExtendedMetric } from "@modules/ServiceProviders/types/serviceProvider.types";
import { MetricCard } from "../MetricCard";
import { MetricIconStatus } from "../MetricIconStatus";
import messages from "../messages";

type TProfilePicValue =
  | "missing"
  | "not_requested"
  | "done"
  | "pending"
  | "refused"
  | null;

type TIconState = "default" | "success" | "error" | "pending";

const profilePicIconState = (value: TProfilePicValue): TIconState => {
  if (value === "done") return "success";
  if (value === "pending") return "pending";
  if (value === "refused") return "error";
  return "default";
};

interface IProfilePicCard {
  metric: IExtendedMetric | undefined;
  onClick?: () => void;
}

export const ProfilePicCard = memo(({ metric, onClick }: IProfilePicCard) => {
  const value = (metric?.value ?? null) as TProfilePicValue;
  const reached = value === "done";
  const refused = value === "refused";
  const pending = value === "pending";
  const imageUrl = value === "missing" ? undefined : metric?.computationDetails?.profilePictureThumbUrl;

  return (
    <MetricCard
      title={<FormattedMessage {...messages.profilePicTitle} />}
      onClick={onClick}
      description={<FormattedMessage {...messages.profilePicDescription} />}
      state={reached ? "reached" : refused ? "rejected" : pending ? "pending" : "not-reached"}
    >
      <MetricIconStatus
        icon={imageUrl ? undefined : FaceViewfinderIcon}
        imageUrl={imageUrl}
        state={profilePicIconState(value)}
        message={
          refused ? (
            <>
              <FormattedMessage {...messages.profilePicStatusRefused} />
              <br />
              <Link as="button" color="pink.500" fontWeight="500" onClick={onClick}>
                <FormattedMessage {...messages.profilePicTryAgain} />
              </Link>
            </>
          ) : (
            <FormattedMessage
              {...(value === "done"
                ? messages.profilePicStatusReached
                : value === "pending"
                ? messages.profilePicStatusPending
                : messages.profilePicStatus)}
            />
          )
        }
      />
    </MetricCard>
  );
});

ProfilePicCard.displayName = "ProfilePicCard";
